import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Ban, Check, Copy, Plus, Save, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import { getAccounts } from '../../api/master'
import { deleteJournalEntry, getJournalEntry, saveJournalEntry, voidJournalEntry } from '../../api/accounting'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDateTime, fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { ACCOUNT_TYPES, STATUS_TONE } from './accountingShared'

let lineKey = 0
const blankLine = (l = {}) => ({ key: ++lineKey, account_code: '', description: '', dr: '', cr: '', ...l })
const back = <BackLink to="/accounting/journal-entries" label="Journal entries" />

// One journal entry. A new entry or a draft opens in the editor; posted,
// voided and system entries are shown read-only, with what can be done.
export default function JournalEntryDetail() {
  const { jeId } = useParams()
  const isNew = jeId === 'new'
  const [entry, setEntry] = useState(null)
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (isNew) { setEntry(null); return }
    setEntry(null)
    setError('')
    getJournalEntry(jeId).then(setEntry).catch((e) => setError(e.message || 'Could not load the entry.'))
  }, [jeId, isNew, version])

  if (isNew) return <JournalEditor key="new" />
  if (error) return <div className="ci-form-page"><DocHeader back={back} title="Journal entry" /><ErrorBanner message={error} /></div>
  if (!entry) return <Spinner label="Loading the entry…" />
  if (entry.can_edit) return <JournalEditor key={jeId} entry={entry} reload={() => setVersion((v) => v + 1)} />
  return <JournalView entry={entry} reload={() => setVersion((v) => v + 1)} />
}

function JournalEditor({ entry, reload }) {
  const nav = useNavigate()
  const location = useLocation()
  const { notify } = useToast()
  const copy = location.state?.copy
  const jeId = entry?.header?.je_id
  const [accounts, setAccounts] = useState([])
  const [date, setDate] = useState(entry?.header?.entry_date?.slice(0, 10) || localToday())
  const [memo, setMemo] = useState(entry?.header?.memo || copy?.memo || '')
  const [lines, setLines] = useState(() => {
    const from = entry?.lines?.length ? entry.lines : (copy?.lines || [])
    const ls = from.map((l) => blankLine({ account_code: l.account_code, description: l.description || '', dr: l.dr ? String(l.dr) : '', cr: l.cr ? String(l.cr) : '' }))
    while (ls.length < 2) ls.push(blankLine())
    return ls
  })
  const [error, setError] = useState('')
  const [lineErrors, setLineErrors] = useState({})
  const [saving, setSaving] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => { getAccounts().then((a) => setAccounts(Array.isArray(a) ? a : [])).catch(() => setAccounts([])) }, [])

  const grouped = useMemo(() => ACCOUNT_TYPES.map(([type, label]) => [label, accounts.filter((a) => a.type === type)]).filter(([, list]) => list.length), [accounts])
  const filled = lines.filter((l) => l.account_code || num(l.dr) || num(l.cr) || l.description.trim())
  const totalDr = filled.reduce((s, l) => s + num(l.dr), 0)
  const totalCr = filled.reduce((s, l) => s + num(l.cr), 0)
  const diff = Math.round((totalDr - totalCr) * 100) / 100
  const balanced = filled.length >= 2 && totalDr > 0 && Math.abs(diff) < 0.005

  const update = (key, patch) => {
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l
      const next = { ...l, ...patch }
      // A line is one side or the other: typing a debit clears its credit.
      if ('dr' in patch && patch.dr !== '') next.cr = ''
      if ('cr' in patch && patch.cr !== '') next.dr = ''
      return next
    }))
  }

  const save = async (post) => {
    setError('')
    setLineErrors({})
    setSaving(post ? 'post' : 'draft')
    try {
      const payload = {
        entry_date: date, memo: memo.trim(), post,
        lines: filled.map((l) => ({ account_code: l.account_code, description: l.description.trim(), dr: num(l.dr), cr: num(l.cr) })),
      }
      const res = await saveJournalEntry(jeId, payload)
      notify(post ? `Posted ${docNo('je', res.je_no)}` : `Draft ${docNo('je', res.je_no)} saved`)
      // A new entry moves to its own address; a saved draft reloads, and
      // shows read-only once posted.
      if (!jeId) nav(`/accounting/journal/${res.je_id}`, { replace: true, state: null })
      else reload()
    } catch (e) {
      setError(e.message || 'Could not save the entry.')
      // Field errors come back as lines.<row among the filled lines>.
      const fields = e.fields || {}
      const byKey = {}
      Object.entries(fields).forEach(([k, v]) => {
        const m = /^lines\.(\d+)$/.exec(k)
        if (m && filled[+m[1]]) byKey[filled[+m[1]].key] = v
      })
      setLineErrors(byKey)
    } finally {
      setSaving('')
    }
  }

  const remove = async () => {
    setSaving('delete')
    try {
      await deleteJournalEntry(jeId)
      notify('Draft deleted')
      nav('/accounting/journal-entries', { replace: true })
    } catch (e) {
      setError(e.message || 'Could not delete the draft.')
      setConfirmDelete(false)
    } finally {
      setSaving('')
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader
        back={back}
        title={<>{jeId ? `Draft ${docNo('je', entry.header.je_no)}` : 'New journal entry'} <HelpTip k="journal" /></>}
        badge={jeId && <StatusBadge label="Draft" tone="muted" />}
        meta="Record a change to the books by hand."
      />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two acc-je-head">
          <label className="ci-form-field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="ci-form-field">
            <span>Description (memo)</span>
            <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="e.g. Accrue October electricity" maxLength={255} />
          </label>
        </div>
      </div>

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">Lines</div>
        </div>
        <div className="ci-form-table-wrap">
          <table className="ci-form-line-table compact acc-je-table lines-cards">
            <thead>
              <tr>
                <th className="col-account">Account</th>
                <th>Description</th>
                <th className="num col-price">Debit <HelpTip k="debit" /></th>
                <th className="num col-price">Credit <HelpTip k="credit" /></th>
                <th className="col-del" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} className={lineErrors[l.key] ? 'has-error' : undefined}>
                  <td data-label="Account">
                    <select className="ci-form-cell-select" value={l.account_code} onChange={(e) => update(l.key, { account_code: e.target.value })} aria-label={`Account, line ${i + 1}`}>
                      <option value="">Choose an account…</option>
                      {grouped.map(([label, list]) => (
                        <optgroup key={label} label={label}>
                          {list.map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {lineErrors[l.key] && <span className="acc-line-error">{lineErrors[l.key]}</span>}
                  </td>
                  <td data-label="Description"><input className="ci-form-cell-input" value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} placeholder="Optional" aria-label={`Description, line ${i + 1}`} /></td>
                  <td data-label="Debit" className="lc-half"><input className="ci-form-cell-input num" value={l.dr} inputMode="decimal" placeholder="0.00" onChange={(e) => update(l.key, { dr: e.target.value })} aria-label={`Debit, line ${i + 1}`} /></td>
                  <td data-label="Credit" className="lc-half"><input className="ci-form-cell-input num" value={l.cr} inputMode="decimal" placeholder="0.00" onChange={(e) => update(l.key, { cr: e.target.value })} aria-label={`Credit, line ${i + 1}`} /></td>
                  <td className="ci-form-del-cell">
                    <button type="button" className="ci-form-del-btn" onClick={() => setLines((ls) => (ls.length > 2 ? ls.filter((x) => x.key !== l.key) : ls.map((x) => (x.key === l.key ? blankLine() : x))))} aria-label={`Remove line ${i + 1}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="acc-je-totals">
                <td colSpan="2">Totals</td>
                <td className="num lc-half" data-label="Debit">{money(totalDr)}</td>
                <td className="num lc-half" data-label="Credit">{money(totalCr)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="ci-form-add-row acc-je-foot">
          <button data-write type="button" className="ci-form-add-line" onClick={() => setLines((ls) => [...ls, blankLine()])}><Plus size={14} />Add line</button>
          <span className={`acc-balance ${balanced ? 'is-ok' : (filled.length ? 'is-off' : '')}`}>
            {balanced
              ? <><Check size={14} aria-hidden="true" /> Balanced</>
              : (totalDr || totalCr)
                ? <>Out by {money(Math.abs(diff))}: {diff > 0 ? 'credits' : 'debits'} are short</>
                : 'Enter the amounts'}
            <HelpTip k="balanced" />
          </span>
        </div>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!saving || !balanced || !memo.trim()} onClick={() => save(true)} title={!memo.trim() ? 'Add a description to post' : undefined}>
            <Check size={14} />{saving === 'post' ? 'Posting…' : 'Post entry'}
          </button>
          <button data-write type="button" className="ci-btn ci-btn-ghost" disabled={!!saving} onClick={() => save(false)}><Save size={14} />{saving === 'draft' ? 'Saving…' : 'Save draft'}</button>
          {jeId
            ? <button data-write type="button" className="ci-btn ci-btn-ghost acc-danger" disabled={!!saving} onClick={() => setConfirmDelete(true)}><Trash2 size={14} />Delete draft</button>
            : <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>}
        </div>
        <div className="ci-form-action-right acc-post-hint">
          Post or save as a draft
          <HelpTip text="Posting puts the entry into the books and the reports; after that it can only be voided. A draft is saved but stays out of the books until you post it." />
        </div>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this draft?"
        subtitle="It was never posted, so the books don’t change."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button>
            <Button variant="danger" loading={saving === 'delete'} onClick={remove}>Delete draft</Button>
          </>
        )}
      />
    </div>
  )
}

function JournalView({ entry, reload }) {
  const nav = useNavigate()
  const { notify } = useToast()
  const { header, lines, source } = entry
  const [confirmVoid, setConfirmVoid] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [nrv, setNrv] = useState(null)
  const totalDr = lines.reduce((s, l) => s + num(l.dr), 0)
  const totalCr = lines.reduce((s, l) => s + num(l.cr), 0)
  const isNrv = lines.some((l) => String(l.description || '').toLowerCase().startsWith('nrv write-down'))

  useEffect(() => {
    if (!isNrv) return
    api.get('/inventory/valuation/je-breakdown', { params: { je_id: header.je_id } })
      .then(({ data }) => setNrv(data?.items || []))
      .catch(() => setNrv([]))
  }, [isNrv, header.je_id])

  const doVoid = async () => {
    setBusy(true)
    setError('')
    try {
      await voidJournalEntry(header.je_id)
      notify(`${docNo('je', header.je_no)} voided`)
      setConfirmVoid(false)
      reload()
    } catch (e) {
      setError(e.message || 'Could not void the entry.')
      setConfirmVoid(false)
    } finally {
      setBusy(false)
    }
  }

  const copyAsNew = () => nav('/accounting/journal/new', { state: { copy: { memo: header.memo, lines: lines.map((l) => ({ account_code: l.account_code, description: l.description, dr: l.dr, cr: l.cr })) } } })

  let note = null
  if (source.kind === 'system') {
    note = <>Made by a {source.label.toLowerCase()}{source.link && <> (<Link to={source.link}>open it</Link>)</>}. To change it, correct the {source.label.toLowerCase()}, so its records and the books stay in step.</>
  } else if (source.kind === 'closing') {
    note = <>Closing entry, made when the month was closed. Reopen the month under <Link to="/accounting/month-end">Month-end close</Link> to undo it. <HelpTip k="closingEntry" /></>
  } else if (entry.period_closed) {
    note = <>This month is closed, so the entry can’t be changed. Reopen it under <Link to="/accounting/month-end">Month-end close</Link> first.</>
  } else if (header.status === 'Voided') {
    note = <>Voided: it’s no longer in the books or the reports.</>
  }

  return (
    <div className="ci-form-page">
      <DocHeader
        back={back}
        title={`${docNo('je', header.je_no)} · ${header.memo || 'Journal entry'}`}
        badge={<StatusBadge label={header.status} tone={STATUS_TONE[header.status]} />}
        meta={(
          <>
            {fmtDay(header.entry_date)}
            {' · '}{source.label} <HelpTip k={source.kind === 'manual' ? 'manual' : 'source'} />
            {header.posted_at && ` · posted ${fmtDateTime(header.posted_at)}`}
          </>
        )}
        actions={(
          <>
            {source.kind === 'manual' && <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={copyAsNew}><Copy size={14} />Copy to a new entry</button>}
            {entry.can_void && <button data-write type="button" className="ci-btn ci-btn-ghost acc-danger" onClick={() => setConfirmVoid(true)}><Ban size={14} />Void</button>}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}
      {note && <p className="doc-hint acc-view-note">{note}</p>}

      <section className="doc-card">
        <div className="doc-card-head">
          <h2>Lines</h2>
          <span>{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
        </div>
        <div className="doc-table-scroll">
          <table className="ci-line-table acc-view-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Description</th>
                <th className="num">Debit <HelpTip k="debit" /></th>
                <th className="num">Credit <HelpTip k="credit" /></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const extra = [l.vendor_name, l.customer_name, l.product_name && `${l.product_sku ? `${l.product_sku} ` : ''}${l.product_name}${l.qty ? ` × ${num(l.qty).toLocaleString()}` : ''}`].filter(Boolean).join(' · ')
                return (
                  <tr key={l.jl_id}>
                    <td>{l.account_code} · {l.account_name || 'Unknown account'}</td>
                    <td>{l.description || '–'}{extra && <span className="cell-sub">{extra}</span>}</td>
                    <td className="num">{num(l.dr) ? money(l.dr) : ''}</td>
                    <td className="num">{num(l.cr) ? money(l.cr) : ''}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="acc-je-totals">
                <td colSpan="2">Totals</td>
                <td className="num lc-half" data-label="Debit">{money(totalDr)}</td>
                <td className="num lc-half" data-label="Credit">{money(totalCr)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {isNrv && (
        <section className="doc-card">
          <div className="doc-card-head"><h2>Products written down</h2><span>Net realizable value (NRV)</span></div>
          {nrv === null ? <Spinner label="Loading…" /> : nrv.length === 0 ? <p className="doc-empty">No product detail for this write-down.</p> : (
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead><tr><th>Product</th><th className="num">Qty</th><th className="num">From cost</th><th className="num">To cost</th><th className="num">Amount</th></tr></thead>
                <tbody>
                  {nrv.map((r, i) => (
                    <tr key={i}>
                      <td>{r.name || r.sku}{r.sku && <span className="cell-sub">{r.sku}</span>}</td>
                      <td className="num">{num(r.qty).toLocaleString()}</td>
                      <td className="num">{money(r.from_cost)}</td>
                      <td className="num">{money(r.to_cost)}</td>
                      <td className="num">{money(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <Modal
        open={confirmVoid}
        onClose={() => setConfirmVoid(false)}
        title={`Void ${docNo('je', header.je_no)}?`}
        subtitle="It comes out of the books and the reports, and stays listed as voided. To record it differently, copy it to a new entry afterwards."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmVoid(false)}>Keep it</Button>
            <Button variant="danger" loading={busy} onClick={doVoid}>Void entry</Button>
          </>
        )}
      />
    </div>
  )
}
